import { Box, Stack, styled, Typography } from '@mui/material'
import { MUIThemeColors } from '../../mui-theme'
import { getThemeColor } from '../../utils'
import { CALENDAR_MIN_WIDTH } from './calendarLayout'

const hairline = 'rgba(100, 116, 139, 0.14)' // subtle grid lines
const muted = 'rgba(71, 85, 105, 0.65)' // slate-500 muted
const ink = 'rgba(15, 23, 42, 0.88)' // slate-900-ish primary text
const todayTint = 'rgba(37, 99, 235, 0.06)'

// Seven equal day columns separated by hairline gaps.
export const WeekGrid = styled(Box)({
  flex: '1 1 0',
  minHeight: 0,
  display: 'grid',
  gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
  minWidth: `${CALENDAR_MIN_WIDTH}px`,
  gap: '1px',
  border: `1px solid ${hairline}`,
  borderRadius: '14px',
  overflow: 'hidden',
  backgroundColor: hairline,
})

// A single day column (white surface inside the hairline grid).
export const DayColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  minWidth: 0,
  backgroundColor: '#fff',
})

// Column header holding the weekday name + date number; tinted for today.
export const DayHeader = styled(Box, { shouldForwardProp: prop => prop !== 'isToday' })<{ isToday: boolean }>(
  ({ theme, isToday }) => ({
    textAlign: 'center',
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${hairline}`,
    backgroundColor: isToday ? todayTint : 'rgba(248, 250, 252, 0.8)',
  }),
)

export const DayName = styled(Typography)({
  display: 'block',
  textTransform: 'uppercase',
  letterSpacing: '0.8px',
  fontWeight: 700,
  color: muted,
})

// Date number badge; filled primary circle for today.
export const DayNumber = styled(Typography, { shouldForwardProp: prop => prop !== 'isToday' })<{ isToday: boolean }>(
  ({ theme, isToday }) => ({
    fontWeight: 700,
    fontSize: '15px',
    lineHeight: '26px',
    marginTop: theme.spacing(0.25),
    marginLeft: 'auto',
    marginRight: 'auto',
    width: 26,
    height: 26,
    borderRadius: '999px',
    color: isToday ? getThemeColor(theme, MUIThemeColors.white) : ink,
    backgroundColor: isToday ? getThemeColor(theme, MUIThemeColors.primaryMain) : 'transparent',
  }),
)

export const DayEventList = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(0.75),
  overflowY: 'auto',
  overflowX: 'hidden',
  minWidth: 0,
  flex: 1,
}))

export const EmptyDay = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  color: 'rgba(148, 163, 184, 0.9)',
  marginTop: theme.spacing(1),
}))

interface WeekEventCardBoxProps {
  tint: string
  text: string
  accent: string
  isProjected: boolean
}

// Event row inside a day column. Colours come from the per-type palette; projected
// (not-yet-persisted) occurrences get a dashed border and reduced opacity.
export const WeekEventCardBox = styled(Box, {
  shouldForwardProp: prop => !['tint', 'text', 'accent', 'isProjected'].includes(prop as string),
})<WeekEventCardBoxProps>(({ theme, tint, text, accent, isProjected }) => ({
  backgroundColor: tint,
  color: text,
  borderLeft: `3px ${isProjected ? 'dashed' : 'solid'} ${accent}`,
  borderRadius: theme.shape.borderRadius * 1.5,
  padding: theme.spacing(0.75, 1),
  minWidth: 0,
  overflow: 'hidden',
  flexShrink: 0,
  cursor: 'pointer',
  opacity: isProjected ? 0.72 : 1,
  transition: 'transform .12s ease, box-shadow .12s ease',
  '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 3px 8px rgba(15, 23, 42, 0.12)' },
}))
